import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { Document, Page } from 'react-pdf';
import { PDF_DOCUMENT_OPTIONS } from '../../../../pdfjsConfig';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';

const formatSize = (size) => {
  const bytes = Number(size || 0);
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
};

export default function PermitAttachmentField({
  workItemId,
  files = [],
  onAttachFiles,
  onRemoveAttachment,
  onResolvePreview,
  readOnly = false,
  compact = false,
}) {
  const dispatch = useDispatch();
  const { statusBar = 0 } = useSelector((state) => state.systemBar);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(320);
  const previewBodyRef = useRef(null);

  useEffect(() => {
    if (!previewFile) return undefined;
    const updateWidth = () => {
      const width = previewBodyRef.current?.clientWidth || window.innerWidth;
      setPageWidth(Math.max(280, Math.min(width - 24, 900)));
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [previewFile]);

  const getExtension = (file) =>
    String(file?.fileName || file?.originalName || file?.saveName || '')
      .split('.')
      .pop()
      .toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
    getExtension(previewFile)
  );

  const openPreview = async (file) => {
    setPreviewFile(file);
    setPreviewLoading(true);
    setPreviewError('');
    setNumPages(0);
    try {
      const resolved = onResolvePreview
        ? await onResolvePreview(file)
        : file.preview || file.url || '';
      if (!resolved) {
        setPreviewError(
          '미리보기를 지원하지 않는 파일입니다. 다운로드해 확인해 주세요.'
        );
      }
      setPreviewUrl(resolved || '');
    } catch (error) {
      setPreviewError(
        error?.message ||
          '문서를 미리보기 형식으로 변환하지 못했습니다. 다운로드해 확인해 주세요.'
      );
      setPreviewUrl('');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewUrl('');
    setPreviewError('');
    setNumPages(0);
  };

  const downloadUrl =
    previewFile?.downloadUrl || previewFile?.originalUrl || previewFile?.preview;

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const downloadFile = async (file) => {
    const fileName =
      file?.fileName || file?.originalName || file?.saveName || 'downloaded_file';
    let filePath = file?.filePath || file?.previewFilePath || '';

    if (!filePath) {
      const sourceUrl = file?.downloadUrl || file?.originalUrl || file?.preview || '';
      filePath = decodeURIComponent(sourceUrl.split('/files/')[1] || '');
    }

    if (!filePath) {
      dispatch(
        showSnackbar({ message: '다운로드할 파일 정보가 없습니다.', type: 'warning' })
      );
      return;
    }

    try {
      const result = await Commons.Axios(
        'fileDown',
        Global.file.useUrl + Global.urls.Execute.UrlFileDownload,
        {
          FileName: fileName,
          Url: Commons.EncodeUrl(filePath),
        }
      );
      const blobData = result?.data;
      const contentType =
        result?.headers?.['content-type'] || 'application/octet-stream';

      if (!blobData) throw new Error('파일 다운로드 응답이 없습니다.');

      const blob = new Blob([blobData], { type: contentType });
      if (Capacitor.isNativePlatform()) {
        const base64Data = await blobToBase64(blob);
        const savedFile = await Filesystem.writeFile({
          path: fileName,
          data: base64Data,
          directory: Directory.Cache,
          recursive: true,
        });
        dispatch(
          showSnackbar({
            message: '저장할 위치를 선택해 주세요.',
            type: 'info',
          })
        );
        await Share.share({
          title: fileName,
          dialogTitle: '파일 저장',
          files: [savedFile.uri],
        });
      } else {
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(objectUrl);
      }

      if (!Capacitor.isNativePlatform()) {
        dispatch(
          showSnackbar({ message: `"${fileName}" 다운로드 완료`, type: 'success' })
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: error?.message || '파일 다운로드에 실패했습니다.',
          type: 'error',
        })
      );
    }
  };

  return (
    <Box sx={{ mt: compact ? 0 : 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>첨부파일</Typography>
        <Typography sx={{ fontSize: 13, color: '#5ABD4F' }}>
          {files.length}개
        </Typography>
      </Box>
      {!readOnly && (
        <>
          <Typography sx={{ mb: 1.25, color: '#858B87', fontSize: 12 }}>
            최대 10개 · 파일당 20MB · 전체 100MB
          </Typography>
          <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-green-400 bg-green-50 py-3 text-sm font-bold text-green-700 active:opacity-70">
            <AttachFileIcon sx={{ fontSize: 19 }} />
            파일 첨부하기
            <input
              hidden
              type="file"
              multiple
              accept=".pdf,.hwp,.hwpx,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
              onChange={async (event) => {
                await onAttachFiles?.(workItemId, event.target.files);
                event.target.value = '';
              }}
            />
          </label>
        </>
      )}

      <Box sx={{ display: 'grid', gap: 1, mt: files.length ? 1.25 : 0 }}>
        {files.map((file, index) => (
          <Box
            key={`${file.filePath || file.previewFilePath || file.fileName}-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              p: 1.25,
              border: '1px solid #E5E8E6',
              borderRadius: 2,
              bgcolor: '#fff',
              minWidth: 0,
            }}
          >
            <InsertDriveFileOutlinedIcon
              sx={{ flexShrink: 0, color: '#5ABD4F', fontSize: 21 }}
            />
            <Box sx={{ minWidth: 0, flex: 1, maxWidth: 'calc(100% - 140px)' }}>
              <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700 }}>
                {file.fileName || file.originalName || file.saveName}
              </Typography>
              <Typography sx={{ color: '#8A908C', fontSize: 11 }}>
                {formatSize(file.fileSize)}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <button
                type="button"
                onClick={() => openPreview(file)}
                className="shrink-0 rounded-md px-1.5 py-1 text-[11px] font-bold text-green-700 active:bg-green-50"
              >
                보기
              </button>
              {(file.downloadUrl || file.preview) && (
                <button
                  type="button"
                  onClick={() => downloadFile(file)}
                  aria-label="파일 다운로드"
                  className="flex shrink-0 items-center justify-center rounded-md p-1 text-slate-600 active:bg-slate-50"
                >
                  <DownloadIcon sx={{ fontSize: 18 }} />
                </button>
              )}
              {!readOnly && (
                <IconButton
                  size="small"
                  aria-label="첨부파일 삭제"
                  onClick={() => onRemoveAttachment?.(workItemId, index)}
                  sx={{ flexShrink: 0, p: 0.5 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Dialog fullScreen open={!!previewFile} onClose={closePreview}>
        <AppBar
          sx={{
            position: 'relative',
            bgcolor: '#fff',
            color: '#202420',
            pt:
              Number(statusBar) > 0
                ? `${Number(statusBar)}px`
                : 'env(safe-area-inset-top, 0px)',
          }}
        >
          <Toolbar>
            <IconButton edge="start" onClick={closePreview}>
              <CloseIcon />
            </IconButton>
            <Typography noWrap sx={{ ml: 1, flex: 1, fontWeight: 700 }}>
              {previewFile?.fileName || previewFile?.originalName || '첨부파일'}
            </Typography>
            {downloadUrl && (
              <IconButton onClick={() => downloadFile(previewFile)}>
                <DownloadIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
        <Box
          ref={previewBodyRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            bgcolor: '#303330',
            p: 1.5,
          }}
        >
          {previewLoading && (
            <Box sx={{ pt: 8, textAlign: 'center', color: '#fff' }}>
              <CircularProgress color="inherit" />
              <Typography sx={{ mt: 2 }}>문서를 준비하고 있습니다.</Typography>
            </Box>
          )}
          {!previewLoading && previewError && (
            <Box sx={{ pt: 8, px: 3, textAlign: 'center', color: '#fff' }}>
              <Typography>{previewError}</Typography>
            </Box>
          )}
          {!previewLoading && !previewError && previewUrl && isImage && (
            <Box
              sx={{
                width: '100%',
                minHeight: 'calc(100vh - 88px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={previewUrl}
                alt={previewFile?.fileName || '첨부 이미지'}
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 112px)',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
          {!previewLoading && !previewError && previewUrl && !isImage && (
            <Document
              file={previewUrl}
              options={PDF_DOCUMENT_OPTIONS}
              loading={
                <Box sx={{ pt: 8, textAlign: 'center', color: '#fff' }}>
                  <CircularProgress color="inherit" />
                </Box>
              }
              onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
              onLoadError={() =>
                setPreviewError(
                  'PDF를 불러오지 못했습니다. 다운로드해 확인해 주세요.'
                )
              }
            >
              {Array.from({ length: numPages }, (_, index) => (
                <Box
                  key={`page-${index + 1}`}
                  sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Box>
              ))}
            </Document>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
